import { useAuth } from '@hooks/use-auth';
import { Award, Contact, FileText, Globe, KeyRound, LogOut, User, Users } from 'lucide-react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

export default function Layout(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, isSuperadmin, isAdmin } = useAuth();
  // The change-password screen is included: it renders its own full-page card, and
  // its whole point is that the rest of the admin is not available yet.
  const isLoginPage =
    location.pathname === '/login' ||
    location.pathname === '/' ||
    location.pathname === '/create-account' ||
    location.pathname === '/change-password';

  const handleLogout = (): void => {
    logout();
    navigate('/', { replace: true });
  };

  if (isLoginPage) {
    return <Outlet />;
  }

  return (
    <>
      <header className="app-header">
        <Link to="/editions" className="app-header__brand">
          <span className="app-header__logo">
            <Award size={18} />
          </span>
          DP Awards
        </Link>

        <nav className="app-header__nav">
          {/* `isAdmin` is true for superadmins too, so this group covers both. */}
          {isAdmin && (
            <>
              <Link to="/sites" className="btn btn-ghost btn-sm" aria-label="Manage sites">
                <Globe size={16} />
                Sites
              </Link>

              <Link to="/pms-voters" className="btn btn-ghost btn-sm" aria-label="PMS voters">
                <KeyRound size={16} />
                PMS Voters
              </Link>
            </>
          )}

          {isSuperadmin && (
            <>
              <Link to="/regular-users" className="btn btn-ghost btn-sm" aria-label="Regular users">
                <Contact size={16} />
                Regular Users
              </Link>

              <Link to="/users" className="btn btn-ghost btn-sm" aria-label="Manage users">
                <Users size={16} />
                Users
              </Link>

              <Link to="/audit-logs" className="btn btn-ghost btn-sm" aria-label="Audit log">
                <FileText size={16} />
                Audit Log
              </Link>
            </>
          )}
        </nav>

        <div className="app-header__user">
          <span>{user?.name ?? 'User'}</span>
          <span className="app-header__avatar">
            <User size={16} />
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-sm"
            aria-label="Log out"
            onClick={handleLogout}
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="app-main">
        <Outlet />
      </main>
    </>
  );
}
